import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, Badge, EmptyState, LoadingState, ErrorState } from "@/components/design-system";
import { listOwnCampaigns, openCampaign, closeCampaign, deleteCampaign } from "@/lib/campaigns";
import { formatApiError } from "@/lib/api";

function StatusBadge({ status }) {
  if (status === "open") return <Badge variant="active">Open</Badge>;
  if (status === "closed") return <Badge variant="neutral">Closed</Badge>;
  return <Badge variant="neutral">Draft</Badge>;
}

export default function CampaignsList() {
  const [state, setState] = useState({ status: "loading", items: [], err: "" });
  const [busy, setBusy] = useState({});

  const load = async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const items = await listOwnCampaigns();
      setState({ status: "ready", items, err: "" });
    } catch (err) { setState({ status: "error", items: [], err: formatApiError(err) }); }
  };
  useEffect(() => { load(); }, []);

  const set = (id, v) => setBusy((b) => ({ ...b, [id]: v }));
  const onOpen = async (c) => { set(c.id, 1); try { const n = await openCampaign(c.id); setState((s) => ({ ...s, items: s.items.map((x) => x.id === n.id ? n : x) })); } catch (e) { alert(formatApiError(e)); } finally { set(c.id, 0); } };
  const onClose = async (c) => { set(c.id, 1); try { const n = await closeCampaign(c.id); setState((s) => ({ ...s, items: s.items.map((x) => x.id === n.id ? n : x) })); } catch (e) { alert(formatApiError(e)); } finally { set(c.id, 0); } };
  const onDelete = async (c) => { if (!window.confirm(`Delete "${c.name}"? Draft only, cannot be undone.`)) return; set(c.id, 1); try { await deleteCampaign(c.id); setState((s) => ({ ...s, items: s.items.filter((x) => x.id !== c.id) })); } catch (e) { alert(formatApiError(e)); } finally { set(c.id, 0); } };

  return (
    <div data-testid="campaigns-list-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Campaigns</p>
          <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">Validation campaigns</h1>
          <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">Run a structured feedback round on any published product. Pick a template, open the campaign, and let the community give you qualified answers.</p>
        </div>
        <Link to="/app/campaigns/new"><Button data-testid="new-campaign-btn" variant="primary">Start campaign</Button></Link>
      </div>

      {state.status === "loading" && <LoadingState label="Loading campaigns…" />}
      {state.status === "error" && <ErrorState detail={state.err} action={<Button variant="secondary" onClick={load}>Retry</Button>} />}
      {state.status === "ready" && state.items.length === 0 && (
        <EmptyState
          title="No campaigns yet"
          description="Start a validation campaign to collect structured feedback from qualified community members."
          action={<Link to="/app/campaigns/new"><Button variant="primary">Start your first campaign</Button></Link>}
        />
      )}
      {state.status === "ready" && state.items.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="campaigns-grid">
          {state.items.map((c) => {
            const pct = c.target_responses ? Math.min(100, Math.round((c.response_count / c.target_responses) * 100)) : null;
            return (
              <Card key={c.id} data-testid={`campaign-card-${c.id}`} className="!p-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={c.status} />
                      <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">{c.template_name}</span>
                    </div>
                    <h3 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A] mt-2 truncate">{c.name}</h3>
                    <p className="text-sm text-[#0B1F3A]/70 mt-1">On <b>{c.product_name}</b></p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-[Montserrat] font-extrabold text-2xl text-[#0B1F3A]">{c.response_count}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/60">responses{c.target_responses ? ` / ${c.target_responses}` : ""}</div>
                  </div>
                </div>
                {pct !== null && (
                  <div className="mt-3 h-1.5 rounded-full bg-[#E8F1FF] overflow-hidden">
                    <div className="h-full bg-[#2D7FF9]" style={{ width: `${pct}%` }} />
                  </div>
                )}
                <div className="mt-5 flex flex-wrap gap-2">
                  <Link to={`/app/campaigns/${c.id}`}><Button variant="secondary" data-testid={`campaign-open-${c.id}`}>Open</Button></Link>
                  {c.status === "draft" && (
                    <>
                      <Button variant="primary" onClick={() => onOpen(c)} disabled={busy[c.id]} data-testid={`campaign-launch-${c.id}`}>Launch</Button>
                      <Button variant="danger" onClick={() => onDelete(c)} disabled={busy[c.id]}>Delete</Button>
                    </>
                  )}
                  {c.status === "open" && (
                    <Button variant="ghost" onClick={() => onClose(c)} disabled={busy[c.id]} data-testid={`campaign-close-${c.id}`}>Close</Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
